import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-toastify';
import axios from 'axios';
import { Store } from '../../Store';
import { getError } from '../../utils';
import { api } from '../../Api';

/**
 * BROADCAST — messages the office pushes into the mobile app.
 *
 * Three levels of interruption, and the screen makes the difference between
 * them impossible to miss, because the third one takes away a member's
 * ability to use the app until they respond:
 *
 *   Quiet         sits in their notification list
 *   Pop up        opens over whatever they are doing, closable
 *   Must confirm  cannot be closed until they scroll to the end and press the
 *                 button — and we keep the record of who pressed it and when
 */

const LEVELS = [
  {
    value: 'normal',
    label: 'Quiet',
    explain: 'It waits in their notification list. Nothing interrupts them.',
    colour: '#0d6efd',
  },
  {
    value: 'forceView',
    label: 'Pop up',
    explain: 'A box opens over whatever they are doing. They can close it.',
    colour: '#fd7e14',
  },
  {
    value: 'forceRead',
    label: 'Must confirm',
    explain:
      'The box cannot be closed. They must scroll to the end and press the button. '
      + 'We record who pressed it and when.',
    colour: '#dc3545',
  },
];

const ROLES = [
  { value: 'member', label: 'Members' },
  { value: 'agent', label: 'Agents' },
  { value: 'manager', label: 'User managers' },
  { value: 'admin', label: 'Admins' },
];

const TONES = [
  { value: 'info', label: 'Normal' },
  { value: 'success', label: 'Good news' },
  { value: 'warning', label: 'Warning' },
  { value: 'danger', label: 'Urgent' },
];

const card = {
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: 12,
  padding: 18,
  marginBottom: 18,
};
const label = { display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#374151' };
const input = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid #d1d5db',
  borderRadius: 8,
  fontSize: 14,
  marginBottom: 14,
};

export default function BroadcastScreen() {
  const { state } = React.useContext(Store);
  const { userInfo } = state;
  const headers = useMemo(
    () => ({ Authorization: `Bearer ${userInfo.token}` }),
    [userInfo.token]
  );

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [level, setLevel] = useState('normal');
  const [tone, setTone] = useState('info');
  const [roles, setRoles] = useState(['member']);
  const [usernames, setUsernames] = useState('');
  const [activeOnly, setActiveOnly] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [actionLabel, setActionLabel] = useState('');
  const [actionRoute, setActionRoute] = useState('');
  const [expiresAt, setExpiresAt] = useState('');

  const [rows, setRows] = useState([]);
  const [detail, setDetail] = useState(null);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(api + '/api/announcements/admin?page=1&pageSize=30', { headers });
      setRows(data.announcements || []);
    } catch (error) {
      toast.error(getError(error));
    }
  }, [headers]);

  useEffect(() => {
    load();
  }, [load]);

  const openDetail = async (id) => {
    try {
      const { data } = await axios.get(api + `/api/announcements/admin/${id}`, { headers });
      setDetail(data);
    } catch (error) {
      toast.error(getError(error));
    }
  };

  const targeted = usernames
    .split(/[\s,]+/)
    .map((u) => u.trim())
    .filter(Boolean);

  const audienceWords = targeted.length
    ? `${targeted.length} named ${targeted.length === 1 ? 'person' : 'people'}`
    : roles.length === ROLES.length
    ? 'everyone'
    : roles.map((r) => ROLES.find((x) => x.value === r)?.label || r).join(', ') || 'nobody';

  const chosen = LEVELS.find((l) => l.value === level);

  const send = async () => {
    if (!title.trim() || !body.trim()) {
      toast.error('A title and a message are both required.');
      return;
    }
    // A message nobody can dismiss deserves one deliberate extra step.
    if (level === 'forceRead') {
      const ok = window.confirm(
        `This will lock ${audienceWords} out of the app until each of them presses `
          + `"I have read this".\n\nSend it?`
      );
      if (!ok) return;
    }

    setSending(true);
    try {
      await axios.post(
        api + '/api/announcements/admin',
        {
          title: title.trim(),
          body: body.trim(),
          level,
          tone,
          imageUrl: imageUrl.trim(),
          actionLabel: actionLabel.trim(),
          actionRoute: actionRoute.trim(),
          expiresAt: expiresAt ? new Date(`${expiresAt}T23:59:59`).toISOString() : null,
          audience: { roles, usernames: targeted, activeOnly },
        },
        { headers }
      );
      toast.success('Sent.');
      setTitle('');
      setBody('');
      setImageUrl('');
      setActionLabel('');
      setActionRoute('');
      load();
    } catch (error) {
      toast.error(getError(error));
    } finally {
      setSending(false);
    }
  };

  const setActive = async (row, active) => {
    try {
      await axios.put(api + `/api/announcements/admin/${row._id}`, { active }, { headers });
      toast.success(active ? 'Showing again.' : 'Stopped.');
      load();
      if (detail && detail._id === row._id) openDetail(row._id);
    } catch (error) {
      toast.error(getError(error));
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.title}"? The record of who confirmed it goes too.`)) return;
    try {
      await axios.delete(api + `/api/announcements/admin/${row._id}`, { headers });
      toast.success('Deleted.');
      setDetail(null);
      load();
    } catch (error) {
      toast.error(getError(error));
    }
  };

  return (
    <div className="container" style={{ maxWidth: 1100, paddingBottom: 60 }}>
      <Helmet>
        <title>Send a message</title>
      </Helmet>

      <h3 style={{ margin: '18px 0 4px' }}>Send a message</h3>
      <p style={{ color: '#6b7280', marginBottom: 18 }}>
        Goes to the mobile app. It appears the next time each phone opens or returns to the app.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.4fr) minmax(0,1fr)', gap: 18 }}>
        {/* ------------------------------------------------------- composer */}
        <div>
          <div style={card}>
            <span style={label}>How hard should this interrupt them?</span>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
              {LEVELS.map((l) => (
                <button
                  key={l.value}
                  type="button"
                  onClick={() => setLevel(l.value)}
                  style={{
                    flex: '1 1 150px',
                    padding: '10px 12px',
                    borderRadius: 10,
                    border: `2px solid ${level === l.value ? l.colour : '#e5e7eb'}`,
                    background: level === l.value ? `${l.colour}12` : '#fff',
                    color: level === l.value ? l.colour : '#374151',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  {l.label}
                </button>
              ))}
            </div>
            <div
              style={{
                background: `${chosen.colour}10`,
                border: `1px solid ${chosen.colour}44`,
                borderRadius: 8,
                padding: 12,
                fontSize: 13,
                color: '#374151',
              }}
            >
              {chosen.explain}
            </div>
          </div>

          <div style={card}>
            <span style={label}>Title</span>
            <input style={input} value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)} />

            <span style={label}>Message</span>
            <textarea
              style={{ ...input, minHeight: 140, resize: 'vertical' }}
              value={body}
              maxLength={4000}
              onChange={(e) => setBody(e.target.value)}
            />

            <span style={label}>Colour</span>
            <select style={input} value={tone} onChange={(e) => setTone(e.target.value)}>
              {TONES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>

            <span style={label}>Picture or GIF address (optional)</span>
            <input style={input} value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="https://…" />

            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <span style={label}>Button text (optional)</span>
                <input style={input} value={actionLabel} onChange={(e) => setActionLabel(e.target.value)} placeholder="Open my wallet" />
              </div>
              <div style={{ flex: 1 }}>
                <span style={label}>Button opens</span>
                <input style={input} value={actionRoute} onChange={(e) => setActionRoute(e.target.value)} placeholder="/member/wallet" />
              </div>
            </div>

            <span style={label}>Stop showing after (optional)</span>
            <input style={input} type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
          </div>

          <div style={card}>
            <span style={label}>Who gets it</span>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 12 }}>
              {ROLES.map((r) => (
                <label key={r.value} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 14 }}>
                  <input
                    type="checkbox"
                    checked={roles.includes(r.value)}
                    onChange={(e) =>
                      setRoles((prev) =>
                        e.target.checked ? [...prev, r.value] : prev.filter((x) => x !== r.value)
                      )
                    }
                  />
                  {r.label}
                </label>
              ))}
            </div>

            <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 14, marginBottom: 12 }}>
              <input type="checkbox" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} />
              Only active IDs
            </label>

            <span style={label}>Or name specific usernames (optional)</span>
            <input
              style={input}
              value={usernames}
              onChange={(e) => setUsernames(e.target.value)}
              placeholder="rokey24, mamun01"
            />
            <div style={{ fontSize: 13, color: '#6b7280' }}>
              Naming people here overrides the groups above. This will go to: <b>{audienceWords}</b>.
            </div>
          </div>

          <button
            type="button"
            onClick={send}
            disabled={sending || !title.trim() || !body.trim()}
            style={{
              width: '100%',
              padding: '13px 18px',
              borderRadius: 10,
              border: 'none',
              background: sending ? '#9ca3af' : chosen.colour,
              color: '#fff',
              fontWeight: 700,
              fontSize: 15,
              cursor: sending ? 'default' : 'pointer',
            }}
          >
            {sending ? 'Sending…' : `Send to ${audienceWords}`}
          </button>
        </div>

        {/* --------------------------------------------------------- history */}
        <div>
          <div style={card}>
            <b style={{ fontSize: 14 }}>Already sent</b>
            <div style={{ marginTop: 12 }}>
              {rows.length === 0 ? (
                <div style={{ color: '#6b7280', fontSize: 13 }}>Nothing sent yet.</div>
              ) : (
                rows.map((r) => (
                  <div
                    key={r._id}
                    onClick={() => openDetail(r._id)}
                    style={{
                      padding: '10px 0',
                      borderTop: '1px solid #f3f4f6',
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                      <b style={{ fontSize: 14, flex: 1 }}>{r.title}</b>
                      <span
                        style={{
                          fontSize: 11,
                          padding: '2px 8px',
                          borderRadius: 20,
                          background: r.active ? '#dcfce7' : '#f3f4f6',
                          color: r.active ? '#166534' : '#6b7280',
                        }}
                      >
                        {r.active ? 'Live' : 'Stopped'}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                      {LEVELS.find((l) => l.value === r.level)?.label} · seen {r.seenCount || 0} ·
                      confirmed {r.readCount || 0}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {detail ? (
            <div style={card}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <b style={{ fontSize: 15, flex: 1 }}>{detail.title}</b>
                <button
                  type="button"
                  onClick={() => setDetail(null)}
                  style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#6b7280' }}
                >
                  close
                </button>
              </div>
              <p style={{ fontSize: 13, color: '#374151', whiteSpace: 'pre-wrap', marginTop: 8 }}>
                {detail.body}
              </p>

              <div style={{ display: 'flex', gap: 10, margin: '12px 0' }}>
                <button
                  type="button"
                  onClick={() => setActive(detail, !detail.active)}
                  style={{
                    flex: 1,
                    padding: '9px 12px',
                    borderRadius: 8,
                    border: '1px solid #d1d5db',
                    background: '#fff',
                    cursor: 'pointer',
                  }}
                >
                  {detail.active ? 'Stop showing' : 'Show again'}
                </button>
                <button
                  type="button"
                  onClick={() => remove(detail)}
                  style={{
                    flex: 1,
                    padding: '9px 12px',
                    borderRadius: 8,
                    border: '1px solid #fecaca',
                    background: '#fff',
                    color: '#dc2626',
                    cursor: 'pointer',
                  }}
                >
                  Delete
                </button>
              </div>

              <b style={{ fontSize: 13 }}>
                Confirmed by {detail.readCount || 0} · seen by {detail.seenCount || 0}
              </b>
              <div style={{ maxHeight: 260, overflowY: 'auto', marginTop: 8 }}>
                {(detail.acknowledgements || []).length === 0 ? (
                  <div style={{ color: '#6b7280', fontSize: 13 }}>Nobody has seen it yet.</div>
                ) : (
                  detail.acknowledgements.map((a, i) => (
                    <div
                      key={`${a.username}-${i}`}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: 12,
                        padding: '5px 0',
                        borderTop: '1px solid #f3f4f6',
                      }}
                    >
                      <span>@{a.username || 'unknown'}</span>
                      <span style={{ color: a.readAt ? '#166534' : '#6b7280' }}>
                        {a.readAt
                          ? `confirmed ${new Date(a.readAt).toLocaleString()}`
                          : `seen ${new Date(a.seenAt).toLocaleString()}`}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
